import { ReactElement } from 'react';
import './Services.scss';
import blumLogo from '../../assets/products/blum-novotest-logo.png';
import caronLogo from '../../assets/products/caron-engineering-logo.png';
import laserSystemImg from '../../assets/products/laser-system.jpg';
import toolProbeImg from '../../assets/products/tool-probe.jpg';
import touchProbeImg from '../../assets/products/touch-probe.jpg';
import workpieceProbeImg from '../../assets/products/workpiece-probe.jpg';
import boreGaugeImg from '../../assets/products/bore-gauge.jpg';
import roughnessGaugeImg from '../../assets/products/roughness-gauge.jpg';
import measurementSoftwareImg from '../../assets/products/measurement-software.jpg';
import dtectItImg from '../../assets/products/dtect-it.jpg';
import smartLightImg from '../../assets/products/smart-light.jpg';
import toolConnectImg from '../../assets/products/tool-connect.jpg';
import miconnectImg from '../../assets/products/miconnect.png';
import tmacImg from '../../assets/products/tmac.png';
import autocompImg from '../../assets/products/autocomp.png';

interface Service {
  id: number;
  title: string;
  description: string;
  image?: string;
}

interface ServiceCategory {
  vendor: string;
  logo: string;
  description: string;
  services: Service[];
}

function Services(): ReactElement {
  const serviceCategories: ServiceCategory[] = [
    {
      vendor: 'Blum Novotest',
      logo: blumLogo,
      description:
        'Optical and tactile production measurement technology for machine tools. Our measuring technology supports customers of various industrial sectors in increasing their productivity, as well as the quality of the produced parts.',
      services: [
        {
          id: 1,
          title: 'Laser System',
          description:
            'Automatically measure tool length radius and input data into the CNC controller without operator intervention',
          image: laserSystemImg,
        },
        {
          id: 2,
          title: 'Tool Setting Probe',
          description:
            "All the benefits of the Contact Tool Setter with the addition of the ability to measure the tool with the spindle running at high RPM's. Also has the ability to measure corner radius and run out",
          image: toolProbeImg,
        },
        {
          id: 3,
          title: 'Digilog Touch Probe',
          description:
            'Fast and automatic inspection of work-piece contours during mass production. Specially designed for the harsh conditions in machine tools',
          image: touchProbeImg,
        },
        {
          id: 4,
          title: 'Blum Work-Piece Probe',
          description:
            'Automatically find part in work envelope and input data into the CNC controller without operator intervention',
          image: workpieceProbeImg,
        },
        {
          id: 5,
          title: 'Bore Gauge',
          description:
            'Check precision bores inside the machine and compensate tool data for the next piece',
          image: boreGaugeImg,
        },
        {
          id: 6,
          title: 'Roughness Gauge',
          description:
            'Surface roughness gauges are used for fast and automatic inspection of work-piece surfaces in machining centers and turning machines',
          image: roughnessGaugeImg,
        },
        {
          id: 7,
          title: 'Measurement Software',
          description:
            'Software to enhance on machine 3D probing by creating touch probe routines from a solid model then creating inspection reports from the results',
          image: measurementSoftwareImg,
        },
      ],
    },
    {
      vendor: 'Caron Engineering',
      logo: caronLogo,
      description:
        'Advanced hardware and software solutions for CNC machines to improve Performance, Productivity, and Profitability. The options offered protect your machine and offer valuable information about your process.',
      services: [
        {
          id: 1,
          title: 'DTECT-IT',
          description:
            'Monitor any area of concern on your CNC machine tool or fixture using Caron Engineering USB sensors',
          image: dtectItImg,
        },
        {
          id: 2,
          title: 'Smart Light',
          description:
            'Gives status of all unattended machines from anywhere on the shop floor',
          image: smartLightImg,
        },
        {
          id: 3,
          title: 'Tool Connect',
          description: 'Tool identification for Machine Tools using RFID',
          image: toolConnectImg,
        },
        {
          id: 4,
          title: 'MiConnect',
          description:
            'MiConnect Technology is custom application builder, designed specifically for the CNC manufacturing environment, to streamline process automation and control',
          image: miconnectImg,
        },
        {
          id: 5,
          title: 'TMAC',
          description:
            'Real-time tool monitoring system that detects tool wear and breakage, optimizes feed rates, and maximizes tool life with adaptive control',
          image: tmacImg,
        },
        {
          id: 6,
          title: 'AutoComp',
          description:
            'Automated tool wear compensation software that reads part measurements and updates CNC offsets in real-time for closed-loop machining',
          image: autocompImg,
        },
      ],
    },
  ];

  return (
    <section className="services" id="services">
      <div className="container">
        <h2 className="section-title">Our Products & Services</h2>
        <p className="section-subtitle">
          Comprehensive solutions to enhance your manufacturing processes
        </p>

        {serviceCategories.map((category, categoryIndex) => (
          <div key={categoryIndex} className="service-category">
            <div className="category-header">
              <img
                src={category.logo}
                alt={`${category.vendor} logo`}
                className="vendor-logo"
              />
              <p className="category-description">{category.description}</p>
            </div>

            <div className="services-grid">
              {category.services.map((service) => (
                <div key={service.id} className="service-card">
                  {service.image && (
                    <div className="service-image">
                      <img src={service.image} alt={service.title} />
                    </div>
                  )}
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

export default Services;
